import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  useTheme,
  useMediaQuery,
  Tabs,
  Tab,
  Checkbox,
  FormControlLabel,
  Tooltip,
  IconButton,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import COLORS from '@/resources/style/Themes';
import { useOrganizationUpdaterProvider } from '@/context_providers/organization_context/OrganizationProvider';
import LoadingSpinner from '@/components/loading/LoadingSpinner';
import {
  addEmergencyEmail,
  removeEmergencyEmail,
  updateEmergencyEmailLanguage,
  addEmergencyContactAuthIds,
  removeEmergencyContactAuthIds,
  toggleAddNewAdminsToEmergencyContacts,
} from '@/repository/OrganizationDAO';
import EmergencyEmailManagement from './EmergencyEmailPane';
import EmergencyRespondersPanel from './EmergencyRespondersPane';
import LoadingData from '@/components/loading/LoadingData';
import { StringKeys } from '@/resources/strings/StringKeys';
import { OrgEmergencyContactsOnAzure } from '@/../generated-client/models';
import { useEmergencyContactRows } from './useEmergencyContactRows';

// Tab Panel Component
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      // Flex column so the active panel fills the remaining page height and the
      // responders table can scroll internally instead of overflowing the page.
      style={
        value === index
          ? { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }
          : undefined
      }
      id={`emergency-tabpanel-${index}`}
      aria-labelledby={`emergency-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box
          sx={{
            py: 3,
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            overflowY: 'auto',
          }}
        >
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `emergency-tab-${index}`,
    'aria-controls': `emergency-tabpanel-${index}`,
  };
}

interface OrganizationEmergencyResponseTeamProps {
  getOrgEmergencyContacts: () => OrgEmergencyContactsOnAzure | null;
  updateOrgEmergencyContacts: (
    updatedContacts: OrgEmergencyContactsOnAzure,
  ) => void;
}

const OrganizationEmergencyResponseTeam = ({
  getOrgEmergencyContacts,
  updateOrgEmergencyContacts,
}: OrganizationEmergencyResponseTeamProps) => {
  const { organization, groups } = useOrganizationUpdaterProvider();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { t } = useTranslation();

  const [apiLoading, setApiLoading] = useState(false);
  const [tabValue, setTabValue] = useState(0);

  const orgEmergencyContacts = getOrgEmergencyContacts();

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Auto-add setting from organization emergency contacts data
  const autoAddNewAdmins =
    orgEmergencyContacts?.add_new_admins_to_emergency_contacts ?? false;

  // Use custom hook to get contact rows
  const { authIdContactRows } = useEmergencyContactRows({
    groups,
    contactAuthIds: orgEmergencyContacts?.contact_auth_ids,
  });

  // Get existing contact auth_ids for filtering
  const existingContactAuthIds = useMemo(() => {
    if (!orgEmergencyContacts?.contact_auth_ids) return [];
    return Object.values(orgEmergencyContacts.contact_auth_ids);
  }, [orgEmergencyContacts]);

  // Convert contact_email_addresses to dictionary format expected by EmergencyEmailManagement
  const emailContacts = useMemo(() => {
    if (!orgEmergencyContacts?.contact_email_addresses) return undefined;

    const emailDict: {
      [key: string]: { email: string; name: string; language_code: string };
    } = {};
    Object.values(orgEmergencyContacts.contact_email_addresses).forEach(
      (contact) => {
        emailDict[contact.email] = {
          email: contact.email,
          name: contact.name,
          language_code: contact.language_code,
        };
      },
    );

    return emailDict;
  }, [orgEmergencyContacts]);

  // Handle adding user contacts
  const handleAddUserContacts = useCallback(
    async (authIds: string[]) => {
      if (!organization || authIds.length === 0) return;

      setApiLoading(true);
      try {
        const updatedContacts = await addEmergencyContactAuthIds(authIds);
        updateOrgEmergencyContacts(updatedContacts);
      } finally {
        setApiLoading(false);
      }
    },
    [organization, updateOrgEmergencyContacts],
  );

  // Handle delete for user contacts
  const handleDeleteUserContacts = useCallback(
    async (authIds: string[]) => {
      if (!organization || authIds.length === 0) return;

      setApiLoading(true);
      try {
        const updatedContacts = await removeEmergencyContactAuthIds(authIds);
        updateOrgEmergencyContacts(updatedContacts);
      } finally {
        setApiLoading(false);
      }
    },
    [organization, updateOrgEmergencyContacts],
  );

  // Handle delete for email contact
  const handleDeleteEmailContact = useCallback(
    async (email: string) => {
      if (!organization) return;

      setApiLoading(true);
      try {
        const updatedContacts = await removeEmergencyEmail(email);
        updateOrgEmergencyContacts(updatedContacts);
      } finally {
        setApiLoading(false);
      }
    },
    [organization, updateOrgEmergencyContacts],
  );

  // Handle update email language
  const handleUpdateEmailLanguage = useCallback(
    async (email: string, languageCode: string) => {
      if (!organization) return;

      setApiLoading(true);
      try {
        const updatedContacts = await updateEmergencyEmailLanguage(
          email,
          languageCode,
        );
        updateOrgEmergencyContacts(updatedContacts);
      } finally {
        setApiLoading(false);
      }
    },
    [organization, updateOrgEmergencyContacts],
  );

  // Handle add email contact
  const handleAddEmailContact = useCallback(
    async (email: string, name: string, languageCode: string) => {
      if (!organization) return;

      setApiLoading(true);
      try {
        const updatedContacts = await addEmergencyEmail(
          email,
          name,
          languageCode,
        );
        updateOrgEmergencyContacts(updatedContacts);
      } finally {
        setApiLoading(false);
      }
    },
    [organization, updateOrgEmergencyContacts],
  );

  // Handle toggle auto-add new admins
  const handleToggleAutoAddNewAdmins = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const newValue = event.target.checked;
    setApiLoading(true);
    try {
      const updatedContacts =
        await toggleAddNewAdminsToEmergencyContacts(newValue);
      updateOrgEmergencyContacts(updatedContacts);
    } finally {
      setApiLoading(false);
    }
  };

  if (!organization) {
    return <LoadingSpinner />;
  }

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        // Sidebar width itself is now excluded by the parent page's wrapper Box
        // (see EmergencyResponderManagement.tsx); this is just the gutter after it.
        ...(isMobile ? { px: 2, pt: 0 } : { pl: '40px', pr: '40px' }),
        position: 'relative',
        zIndex: 1,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box sx={{ position: 'relative', display: 'inline-block' }}>
          <Typography
            sx={{
              top: -12,
              ml: '2px',
              fontSize: 16,
              fontWeight: 500,
              color: COLORS.TEXT_HEADER_GRAY,
              lineHeight: 1,
            }}
          >
            {t(StringKeys.COMPANY_WIDE_EMERGENCY_RESPONDERS)}
          </Typography>

          <Typography
            variant="h4"
            component="h1"
            sx={{
              color: COLORS.SAFEGUARD_BLUE,
              fontWeight: 400,
            }}
          >
            {organization.company_name}
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          mt: 0,
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Auto-add new admins checkbox */}
        <Box sx={{ mt: 1 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={autoAddNewAdmins}
                onChange={handleToggleAutoAddNewAdmins}
                disabled={apiLoading}
                sx={{
                  color: COLORS.DARK_BLUE,
                  '&.Mui-checked': {
                    color: COLORS.SAFEGUARD_BLUE,
                  },
                }}
              />
            }
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography variant="body2">
                  {t(StringKeys.ADD_NEW_ADMINS_AS_RESPONDERS)}
                </Typography>
                <Tooltip
                  title={t(StringKeys.ADD_NEW_ADMINS_AS_RESPONDERS_TOOLTIP)}
                >
                  <IconButton size="small" sx={{ p: 0 }}>
                    <InfoOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            }
          />
        </Box>

        {/* Create reusable tabs component */}
        {(() => {
          const tabsComponent = (
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
              aria-label="emergency contacts tabs"
            >
              <Tab
                label={`${t(StringKeys.EMERGENCY_RESPONDERS)} (${authIdContactRows.length})`}
                {...a11yProps(0)}
              />
              <Tab
                label={`${t(StringKeys.EMAILS)} (${emailContacts ? Object.keys(emailContacts).length : 0})`}
                {...a11yProps(1)}
              />
            </Tabs>
          );

          return (
            <>
              {/* Responders Tab Panel */}
              <CustomTabPanel value={tabValue} index={0}>
                <EmergencyRespondersPanel
                  contacts={authIdContactRows}
                  onAddContacts={handleAddUserContacts}
                  onDeleteContacts={handleDeleteUserContacts}
                  loading={apiLoading}
                  groups={groups || []}
                  currentGroupId="" // No group filtering for org-level
                  existingContactAuthIds={existingContactAuthIds}
                  tabControl={tabsComponent}
                  emptyMessage={t(
                    StringKeys.NO_EMERGENCY_RESPONDERS_CONFIGURED,
                  )}
                />
              </CustomTabPanel>

              {/* Email Contacts Tab Panel */}
              <CustomTabPanel value={tabValue} index={1}>
                <EmergencyEmailManagement
                  emailContacts={emailContacts}
                  onDelete={handleDeleteEmailContact}
                  onUpdateLanguage={handleUpdateEmailLanguage}
                  onAddEmail={handleAddEmailContact}
                  loading={apiLoading}
                  emptyMessage={t(StringKeys.NO_EMAIL_CONTACTS_CONFIGURED)}
                  tabControl={tabsComponent}
                />
              </CustomTabPanel>
            </>
          );
        })()}
      </Box>
      <LoadingData show={apiLoading} />
    </Box>
  );
};

export default OrganizationEmergencyResponseTeam;
